 import React, { useEffect, useRef, useCallback } from "react";
import * as d3 from "d3";
import "./FileMapTab.scss";
import { useDispatch, useSelector } from "react-redux";
import { isNull } from "lodash";
import { GET_FILE_MAP_DATA } from "../DataTagConstants";
import Page_Loader from "../../../assets/vizr_images/page_loader.gif";

const W = 800;
const H = 500;
const CX = W / 2;
const CY = H / 2;
const OWNER_R = 52;
const BLOOM_R = 185;
const MIN_NODE_R = 22;
const MAX_NODE_R = 52;

const SENSITIVITY_META = {
  CRITICAL: {
    label: "Critical",
    fill: "#E22D1B",
    light: "#febbcb",
    text: "#E22D1B",
  },
  HIGH: { label: "High", fill: "#ff2251", light: "#febbcb", text: "#ff2251" },
  MEDIUM: {
    label: "Medium",
    fill: "#ff8421",
    light: "#fde5d3",
    text: "#ff8421",
  },
  LOW: { label: "Low", fill: "#081981", light: "#A8D5FF", text: "#081981" },
  NOT_PROCESSED: {
    label: "Pending",
    fill: "#6B7280",
    light: "#F1EFE8",
    text: "#5F5E5A",
  },
  NO_SENSITIVITY: {
    label: "No sensitivity",
    fill: "#888780",
    light: "#F1EFE8",
    text: "#888780",
  },
};

function sensitivityMeta(cat) {
  return SENSITIVITY_META[cat] ?? SENSITIVITY_META.NO_SENSITIVITY;
}

function formatSize(bytes = 0) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 ** 3) return `${(bytes / 1024 ** 2).toFixed(1)} MB`;
  return `${(bytes / 1024 ** 3).toFixed(1)} GB`;
}

function getInitials(name = "") {
  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("");
}

const HIDDEN_SENSITIVITIES = new Set(["NO_SENSITIVITY", "NOT_PROCESSED"]);

function computeLayout(data, drive) {
  // All entries count toward the owner total, but only non-excluded ones get nodes
  const totalFiles = data.reduce((s, d) => s + d.totalFiles, 0);
  const visibleData = data.filter(
    (d) => !HIDDEN_SENSITIVITIES.has(d.sensitivity),
  );
  const maxFiles = Math.max(...visibleData.map((d) => d.totalFiles), 1);

  function nodeR(count) {
    return MIN_NODE_R + (count / maxFiles) * (MAX_NODE_R - MIN_NODE_R);
  }

  const sensitivityNodes = visibleData.map((item, i) => {
    const angle = ((2 * Math.PI) / visibleData.length) * i - Math.PI / 2;
    const meta = sensitivityMeta(item.sensitivity);
    const r = nodeR(item.totalFiles);
    return {
      id: item.sensitivity,
      sensitivity: item.sensitivity,
      totalFiles: item.totalFiles,
      affectedUsers: item.affectedUsers,
      totalSize: item.totalSize,
      role: "sensitivity",
      r,
      fill: meta.fill,
      meta,
      ox: Math.cos(angle) * BLOOM_R,
      oy: Math.sin(angle) * BLOOM_R,
      x: CX + Math.cos(angle) * BLOOM_R,
      y: CY + Math.sin(angle) * BLOOM_R,
      fx: CX + Math.cos(angle) * BLOOM_R,
      fy: CY + Math.sin(angle) * BLOOM_R,
    };
  });

  const ownerNode = {
    id: "owner",
    label: getInitials(drive?.driveName ?? ""),
    fileName: drive?.driveName ?? "Drive",
    role: "owner",
    r: OWNER_R,
    fill: "#534AB7",
    totalFiles,
    ox: 0,
    oy: 0,
    x: CX,
    y: CY,
    fx: CX,
    fy: CY,
  };

  return { ownerNode, sensitivityNodes };
}

function createTooltip(container) {
  const tt = document.createElement("div");
  tt.className = "fmap-tooltip";
  tt.style.cssText =
    "position:absolute;pointer-events:none;opacity:0;transition:opacity 0.15s;z-index:10;";
  container.appendChild(tt);
  return tt;
}

function showTooltip(tt, node) {
  const { meta } = node;
  tt.innerHTML = `
    <div class="fmap-tt__header">
      <span class="fmap-tt__initial" style="background:${meta.fill}">${meta.label[0]}</span>
      <span class="fmap-tt__name">${meta.label}</span>
    </div>
    <div class="fmap-tt__chip" style="background:${meta.light};color:${meta.text}">${meta.label}</div>
    <div class="fmap-tt__rows">
      <div class="fmap-tt__row">
        <span class="fmap-tt__key">Total files</span>
        <span class="fmap-tt__val">${node.totalFiles}</span>
      </div>
      <div class="fmap-tt__row">
        <span class="fmap-tt__key">Affected users</span>
        <span class="fmap-tt__val">${node.affectedUsers}</span>
      </div>
      <div class="fmap-tt__row">
        <span class="fmap-tt__key">Total size</span>
        <span class="fmap-tt__val">${formatSize(node.totalSize)}</span>
      </div>
    </div>
    <div class="fmap-tt__hint">Click to filter table</div>
  `;
  tt.style.opacity = "1";
}

function positionTooltip(tt, event, containerRect) {
  const TW = 220,
    TH = 175;
  const mx = event.clientX - containerRect.left;
  const my = event.clientY - containerRect.top;
  let left = mx + 14;
  let top = my - 20;
  if (left + TW > containerRect.width - 8) left = mx - TW - 14;
  if (top + TH > containerRect.height - 8) top = my - TH - 10;
  if (top < 4) top = 4;
  tt.style.left = `${left}px`;
  tt.style.top = `${top}px`;
}

function hideTooltip(tt) {
  tt.style.opacity = "0";
}

function buildFileGraph(container, data, drive, onSensitivityClick) {
  d3.select(container).selectAll("*").remove();
  if (!data || data.length === 0) return;

  const { ownerNode, sensitivityNodes } = computeLayout(data, drive);
  const allNodes = [ownerNode, ...sensitivityNodes];
  const links = sensitivityNodes.map((n) => ({ source: ownerNode, target: n }));

  const tt = createTooltip(container);

  const svg = d3
    .select(container)
    .append("svg")
    .attr("width", "100%")
    .attr("height", "100%")
    .attr("viewBox", `0 0 ${W} ${H}`)
    .style("overflow", "hidden")
    .attr("aria-label", "Sensitivity file relationship graph");

  const zoomBehaviour = d3
    .zoom()
    .scaleExtent([0.2, 4])
    .filter((event) => {
      if (event.type === "mousedown")
        return event.target === svg.node() || event.target.tagName === "svg";
      return true;
    })
    .on("zoom", (event) => root.attr("transform", event.transform));

  svg.call(zoomBehaviour);
  svg.call(zoomBehaviour).on("dblclick.zoom", null);
  svg.on("dblclick", () => container.__reset?.());

  container.__zoomIn = () =>
    svg.transition().duration(280).call(zoomBehaviour.scaleBy, 1.4);
  container.__zoomOut = () =>
    svg.transition().duration(280).call(zoomBehaviour.scaleBy, 0.72);
  container.__reset = () =>
    svg
      .transition()
      .duration(380)
      .call(zoomBehaviour.transform, d3.zoomIdentity);

  // Arrow marker
  const defs = svg.append("defs");
  defs
    .append("marker")
    .attr("id", "arr-file")
    .attr("viewBox", "0 0 10 10")
    .attr("refX", 9)
    .attr("refY", 5)
    .attr("markerWidth", 6)
    .attr("markerHeight", 6)
    .attr("orient", "auto")
    .append("path")
    .attr("d", "M1 1L9 5L1 9")
    .attr("fill", "none")
    .attr("stroke", "#888780")
    .attr("stroke-width", "1.5")
    .attr("stroke-linecap", "round")
    .attr("stroke-linejoin", "round");

  const root = svg.append("g").attr("class", "graph-root");
  const edgeLayer = root.append("g").attr("class", "edges");
  const nodeLayer = root.append("g").attr("class", "nodes");

  function edgeCoords(link) {
    const sx = link.source.fx ?? link.source.x;
    const sy = link.source.fy ?? link.source.y;
    const tx = link.target.fx ?? link.target.x;
    const ty = link.target.fy ?? link.target.y;
    const dx = tx - sx,
      dy = ty - sy;
    const dist = Math.sqrt(dx * dx + dy * dy) || 1;
    const ux = dx / dist,
      uy = dy / dist;
    return {
      x1: sx + ux * (link.source.r + 6),
      y1: sy + uy * (link.source.r + 6),
      x2: tx - ux * (link.target.r + 12),
      y2: ty - uy * (link.target.r + 12),
    };
  }

  const edgeSel = edgeLayer
    .selectAll("line")
    .data(links)
    .enter()
    .append("line")
    .attr("stroke", (d) => d.target.fill)
    .attr("stroke-width", 1.2)
    .attr("opacity", 0.4)
    .attr("fill", "none")
    .attr("marker-end", "url(#arr-file)");

  function renderEdges() {
    links.forEach((l) => {
      l._c = edgeCoords(l);
    });
    edgeSel
      .attr("x1", (d) => d._c.x1)
      .attr("y1", (d) => d._c.y1)
      .attr("x2", (d) => d._c.x2)
      .attr("y2", (d) => d._c.y2);
  }

  const nodeGroups = nodeLayer
    .selectAll("g")
    .data(allNodes)
    .enter()
    .append("g")
    .attr("class", "node-g")
    .attr("transform", (d) => `translate(${d.fx},${d.fy})`);

  // Owner dashed ring
  nodeGroups
    .filter((d) => d.role === "owner")
    .append("circle")
    .attr("r", OWNER_R + 10)
    .attr("fill", "none")
    .attr("stroke", "#534AB7")
    .attr("stroke-width", 1.8)
    .attr("stroke-dasharray", "5 3")
    .attr("opacity", 0.4)
    .attr("class", "node-ring");

  // Main circle
  nodeGroups
    .append("circle")
    .attr("r", (d) => d.r)
    .attr("fill", (d) => d.fill)
    .attr("class", "node-main");

  // Text: initials (owner) or label (sensitivity)
  nodeGroups
    .append("text")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "central")
    .attr("y", (d) => (d.role === "owner" ? -6 : -5))
    .attr("fill", "#ffffff")
    .attr("font-size", (d) => (d.role === "owner" ? 16 : 10))
    .attr("font-weight", "500")
    .attr("pointer-events", "none")
    .text((d) => (d.role === "owner" ? d.label : d.meta.label));

  // Owner sub-label: total files
  nodeGroups
    .filter((d) => d.role === "owner")
    .append("text")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "central")
    .attr("y", 11)
    .attr("fill", "rgba(255,255,255,0.65)")
    .attr("font-size", 9)
    .attr("pointer-events", "none")
    .text((d) => `${d.totalFiles} files`);

  // File count badge — top-right of sensitivity nodes
  const badgeG = nodeGroups.filter((d) => d.role === "sensitivity");

  badgeG
    .append("circle")
    .attr("cx", (d) => d.r * 0.7)
    .attr("cy", (d) => -d.r * 0.7)
    .attr("r", 10)
    .attr("fill", "#ffffff")
    .attr("stroke", (d) => d.fill)
    .attr("stroke-width", 0.8);

  badgeG
    .append("text")
    .attr("x", (d) => d.r * 0.7)
    .attr("y", (d) => -d.r * 0.7)
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "central")
    .attr("fill", (d) => d.fill)
    .attr("font-size", 8)
    .attr("font-weight", "600")
    .attr("pointer-events", "none")
    .text((d) => d.totalFiles);

  // Label below sensitivity node
  nodeGroups
    .filter((d) => d.role === "sensitivity")
    .append("text")
    .attr("text-anchor", "middle")
    .attr("dominant-baseline", "hanging")
    .attr("y", (d) => d.r + 8)
    .attr("fill", "var(--color-text-primary)")
    .attr("font-size", 11)
    .attr("font-weight", "400")
    .attr("pointer-events", "none")
    .text((d) => d.meta.label);

  // Hover — sensitivity nodes
  nodeGroups
    .filter((d) => d.role === "sensitivity")
    .on("mouseenter", function (event, d) {
      d3.select(this)
        .select(".node-main")
        .transition()
        .duration(130)
        .attr("r", d.r * 1.1)
        .attr("fill", d3.color(d.fill).brighter(0.3));
      showTooltip(tt, d);
      positionTooltip(tt, event, container.getBoundingClientRect());
    })
    .on("mousemove", function (event) {
      positionTooltip(tt, event, container.getBoundingClientRect());
    })
    .on("mouseleave", function (_, d) {
      d3.select(this)
        .select(".node-main")
        .transition()
        .duration(130)
        .attr("r", d.r)
        .attr("fill", d.fill);
      hideTooltip(tt);
    });

  // Hover — owner node
  nodeGroups
    .filter((d) => d.role === "owner")
    .on("mouseenter", function () {
      d3.select(this)
        .select(".node-main")
        .transition()
        .duration(130)
        .attr("r", OWNER_R * 1.06)
        .attr("fill", d3.color("#534AB7").brighter(0.25));
      d3.select(this)
        .select(".node-ring")
        .transition()
        .duration(130)
        .attr("opacity", 0.7);
    })
    .on("mouseleave", function () {
      d3.select(this)
        .select(".node-main")
        .transition()
        .duration(130)
        .attr("r", OWNER_R)
        .attr("fill", "#534AB7");
      d3.select(this)
        .select(".node-ring")
        .transition()
        .duration(130)
        .attr("opacity", 0.4);
    });

  let dragMoved = false;

  const drag = d3
    .drag()
    .on("start", function (event) {
      dragMoved = false;
      event.sourceEvent.stopPropagation();
      hideTooltip(tt);
      d3.select(this).raise();
    })
    .on("drag", function (event, d) {
      dragMoved = true;
      if (d.role === "owner") {
        const dx = event.dx,
          dy = event.dy;
        allNodes.forEach((n) => {
          n.fx = (n.fx ?? n.x) + dx;
          n.fy = (n.fy ?? n.y) + dy;
        });
        nodeGroups.attr("transform", (n) => `translate(${n.fx},${n.fy})`);
      } else {
        d.fx = event.x;
        d.fy = event.y;
        d3.select(this).attr("transform", `translate(${d.fx},${d.fy})`);
      }
      renderEdges();
    })
    .on("end", function (_, d) {
      if (!dragMoved && d.role === "sensitivity") {
        onSensitivityClick?.(d.sensitivity);
      }
    });

  nodeGroups.call(drag);
  renderEdges();
  container.__stopSim = () => {};
}

export default function FileMapTab({
  dataSourceId,
  actorId,
  drive,
  onSensitivityClick,
}) {
  const containerRef = useRef(null);
  const dispatch = useDispatch();
  const fileMapData = useSelector((state) => state.dataTagReducer.fileMapData);
  const loader = useSelector((state) => state.dataTagReducer.popupLoader);

  const handleClick = useCallback(
    (sensitivity) => {
      if (typeof onSensitivityClick === "function")
        onSensitivityClick(sensitivity);
    },
    [onSensitivityClick],
  );

  useEffect(() => {
    if (!isNull(dataSourceId) && !isNull(actorId)) {
      dispatch({ type: GET_FILE_MAP_DATA, payload: { dataSourceId, actorId } });
    }
  }, []);

  useEffect(() => {
    if (!containerRef.current) return;
    buildFileGraph(
      containerRef.current,
      fileMapData?.content ?? (Array.isArray(fileMapData) ? fileMapData : []),
      drive,
      handleClick,
    );
    return () => containerRef.current?.__stopSim?.();
  }, [fileMapData, drive, handleClick]);

  const hasData = Array.isArray(fileMapData)
    ? fileMapData.length > 0
    : (fileMapData?.content?.length ?? 0) > 0;

  return (
    <div className="fmap-tab">
      {loader && (
        <img
          src={Page_Loader}
          alt="loading"
          className="loader-data-source dataTagPopupLoader"
        />
      )}
      {!hasData ? (
        <div className="fmap-tab__empty">No duplicate file data available</div>
      ) : (
        <>
          <div className="fmap-tab__canvas" ref={containerRef} />
          <div className="fmap-tab__toolbar">
            <div className="fmap-tab__legend">
              {Object.entries(SENSITIVITY_META)
                .filter(([key]) => !HIDDEN_SENSITIVITIES.has(key))
                .map(([key, meta]) => (
                  <span key={key} className="fmap-tab__legend-item">
                    <span
                      className="fmap-dot"
                      style={{ background: meta.fill }}
                    />
                    {meta.label}
                  </span>
                ))}
              <span className="fmap-tab__legend-item">
                <span className="fmap-dot fmap-dot--owner" /> Drive owner
              </span>
            </div>
            <div className="fmap-tab__zoom-controls">
              <button
                className="fmap-tab__zoom-btn"
                title="Zoom in"
                onClick={() => containerRef.current?.__zoomIn?.()}
              >
                +
              </button>
              <button
                className="fmap-tab__zoom-btn"
                title="Zoom out"
                onClick={() => containerRef.current?.__zoomOut?.()}
              >
                −
              </button>
              <button
                className="fmap-tab__zoom-btn fmap-tab__zoom-btn--reset"
                title="Reset view"
                onClick={() => containerRef.current?.__reset?.()}
              >
                ⤢
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
